import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Wallet, Smartphone, RefreshCw, Check, X, Calendar, Zap, Settings as SettingsIcon } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminBusRefunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);  // booking being processed
  const [decision, setDecision] = useState({ approve: true, notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const [autoApprove, setAutoApprove] = useState(null);
  const [showAutoApprove, setShowAutoApprove] = useState(false);

  const fetchAutoApprove = async () => {
    try {
      const { data } = await api.get('/admin/bus/refunds/auto-approve/settings');
      setAutoApprove(data);
    } catch {
      setAutoApprove({ enabled: false, walletThreshold: 200, delayMinutes: 5 });
    }
  };

  const updateAutoApprove = async (patch) => {
    try {
      const { data } = await api.put('/admin/bus/refunds/auto-approve/settings', patch);
      setAutoApprove(data);
      toast({ title: 'Auto-approve settings updated' });
    } catch {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const runAutoApproveNow = async () => {
    try {
      const { data } = await api.post('/admin/bus/refunds/auto-approve/run-now');
      toast({ title: 'Sweep complete', description: `Approved ${data.approved} of ${data.evaluated}` });
      fetchPending();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const fetchPending = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/bus/refunds/pending');
      setRefunds(data.refunds || []);
    } catch {
      toast({ title: 'Failed to load refunds', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); fetchAutoApprove(); }, []);

  const submitDecision = async () => {
    if (!active) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/bus/refunds/${active.id}/process?approve=${decision.approve}&notes=${encodeURIComponent(decision.notes || '')}`
      );
      toast({
        title: decision.approve ? 'Refund approved' : 'Refund denied',
        description: data.method === 'wallet'
          ? `K${data.amount?.toFixed(2)} credited to wallet`
          : data.method === 'mobile_money'
          ? `K${data.amount?.toFixed(2)} queued for Mobile Money payout`
          : 'Decision recorded',
      });
      setActive(null);
      fetchPending();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Could not process', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5" data-testid="admin-bus-refunds-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wallet className="h-6 w-6 text-pink-600" /> Bus Refunds
            </h1>
            <p className="text-sm text-gray-500">Pending refund requests from cancelled bookings.</p>
          </div>
          <Button variant="outline" onClick={() => { setShowAutoApprove(true); fetchAutoApprove(); }} data-testid="auto-approve-settings-btn">
            <Zap className={`h-4 w-4 mr-1 ${autoApprove?.enabled ? 'text-amber-500' : ''}`} /> Auto-approve
            {autoApprove?.enabled && <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-emerald-500" />}
          </Button>
          <Button variant="outline" onClick={fetchPending} data-testid="bus-refunds-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {loading && <p className="text-gray-500">Loading…</p>}
        {!loading && refunds.length === 0 && (
          <Card className="p-10 text-center text-gray-500">
            <Check className="h-10 w-10 mx-auto text-green-500 mb-2" /> No pending refunds.
          </Card>
        )}

        <div className="grid gap-3" data-testid="bus-refunds-list">
          {refunds.map((b) => {
            const c = b.cancellation || {};
            return (
              <Card key={b.id} className="p-4" data-testid={`bus-refund-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold text-sm">{b.ticketCode}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                        Pending {c.refundPercent}%
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium flex items-center gap-1">
                        {c.refundMethod === 'mobile_money' ? <Smartphone className="h-3 w-3" /> : <Wallet className="h-3 w-3" />}
                        {c.refundMethod}
                      </span>
                      <span className="text-xs text-gray-500">by {c.initiatedBy || 'user'}</span>
                    </div>
                    <p className="text-sm font-semibold">K{c.refundAmount?.toFixed(2)} <span className="text-xs text-gray-500 font-normal">/ K{b.pricing?.total?.toFixed(2)} paid</span></p>
                    <p className="text-xs text-gray-600">
                      {b.user?.name || 'User'} · {b.user?.phone} · Operator: {b.operatorName}
                    </p>
                    <p className="text-xs text-gray-500">Reason: {c.reason || '—'}</p>
                    {c.mobileMoneyPhone && <p className="text-xs text-gray-500">MM payout to: {c.mobileMoneyPhone}</p>}
                    <p className="text-[11px] text-gray-400 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Requested {c.requestedAt ? new Date(c.requestedAt).toLocaleString() : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm" variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => { setActive(b); setDecision({ approve: false, notes: '' }); }}
                      data-testid={`refund-deny-${b.id}`}
                    >
                      <X className="h-4 w-4 mr-1" /> Deny
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => { setActive(b); setDecision({ approve: true, notes: '' }); }}
                      data-testid={`refund-approve-${b.id}`}
                    >
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <Dialog open={showAutoApprove} onOpenChange={setShowAutoApprove}>
          <DialogContent className="sm:max-w-md" data-testid="auto-approve-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" /> Auto-approve wallet refunds
              </DialogTitle>
              <DialogDescription>
                Wallet credits are reversible and zero financial risk — let the system clear small refunds automatically.
              </DialogDescription>
            </DialogHeader>
            {!autoApprove ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded border">
                  <div>
                    <p className="font-medium text-sm">Enabled</p>
                    <p className="text-xs text-gray-500">Cron sweep runs every 1 min.</p>
                  </div>
                  <Switch
                    checked={!!autoApprove.enabled}
                    onCheckedChange={(v) => updateAutoApprove({ enabled: v })}
                    data-testid="auto-approve-enabled-toggle"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium block mb-1">Wallet threshold (K)</label>
                    <Input
                      type="number" min="0"
                      value={autoApprove.walletThreshold}
                      onChange={(e) => setAutoApprove({ ...autoApprove, walletThreshold: parseFloat(e.target.value) || 0 })}
                      onBlur={() => updateAutoApprove({ walletThreshold: autoApprove.walletThreshold })}
                      data-testid="auto-approve-threshold-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Grace delay (minutes)</label>
                    <Input
                      type="number" min="0" max="60"
                      value={autoApprove.delayMinutes}
                      onChange={(e) => setAutoApprove({ ...autoApprove, delayMinutes: parseInt(e.target.value, 10) || 0 })}
                      onBlur={() => updateAutoApprove({ delayMinutes: autoApprove.delayMinutes })}
                      data-testid="auto-approve-delay-input"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Refunds above the threshold or paid via Mobile Money still require manual approval. Admin can deny within the grace window to override.
                </p>
                <Button
                  onClick={runAutoApproveNow}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  data-testid="auto-approve-run-now-btn"
                >
                  <Zap className="h-4 w-4 mr-1" /> Run sweep now
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
          <DialogContent className="sm:max-w-md" data-testid="refund-decision-dialog">
            <DialogHeader>
              <DialogTitle>{decision.approve ? 'Approve refund' : 'Deny refund'}</DialogTitle>
              <DialogDescription>
                {active?.ticketCode} · K{(active?.cancellation?.refundAmount || 0).toFixed(2)} via {active?.cancellation?.refundMethod}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {decision.approve && active?.cancellation?.refundMethod === 'wallet' && (
                <p className="text-xs bg-green-50 text-green-900 p-2 rounded">
                  ✓ Will instantly credit K{active.cancellation.refundAmount.toFixed(2)} to the user's Tekstar wallet.
                </p>
              )}
              {decision.approve && active?.cancellation?.refundMethod === 'mobile_money' && (
                <p className="text-xs bg-amber-50 text-amber-900 p-2 rounded">
                  ⚠ Will queue a Mobile Money payout to {active.cancellation.mobileMoneyPhone || 'user phone'} for finance to settle.
                </p>
              )}
              <div>
                <label className="text-xs font-medium block mb-1">Notes (optional)</label>
                <textarea
                  value={decision.notes}
                  onChange={(e) => setDecision({ ...decision, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={3}
                  placeholder={decision.approve ? 'Approval note (visible to user via WhatsApp)' : 'Reason for denial'}
                  data-testid="refund-decision-notes"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setActive(null)}>Cancel</Button>
                <Button
                  className={`flex-1 ${decision.approve ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                  onClick={submitDecision}
                  disabled={submitting}
                  data-testid="refund-decision-submit"
                >
                  {submitting ? 'Processing…' : decision.approve ? 'Confirm approval' : 'Confirm denial'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminBusRefunds;
